import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PlusIcon, BookOpenIcon } from "lucide-react";
import { useBlogsWithPosts } from "./api";
import { useBlogStore } from "./store";
import { availableLanguages } from "../shared";
import { Button } from "./components/Button";

export function Home() {
  const { selectedBlog, setSelectedBlog } = useBlogStore();
  const [tab, setTab] = useState<"published" | "draft">("published");
  const [languageFilter, setLanguageFilter] = useState<string | null>(null);

  const successCallback = useCallback(
    (blogs: any) => {
      if (selectedBlog || !blogs.length) return;
      const [blog] = blogs;
      if (!blog) return;
      setSelectedBlog(blog.name);
    },
    [selectedBlog]
  );

  const { data: blogs = [], loading } = useBlogsWithPosts({ onSuccess: successCallback });

  const selectedBlogData = useMemo(() => {
    if (!blogs || !selectedBlog) return null;
    return blogs.find((blog) => blog.name === selectedBlog) || null;
  }, [blogs, selectedBlog]);

  const filteredPosts = useMemo(() => {
    if (!selectedBlogData?.posts) return [];
    return selectedBlogData.posts.filter((post) => {
      const statusMatch = post.status === tab;
      const languageMatch = !languageFilter || post.language === languageFilter;
      return statusMatch && languageMatch;
    });
  }, [selectedBlogData, tab, languageFilter]);

  const languageCounts = useMemo(() => {
    if (!selectedBlogData?.posts) return { all: 0, byLanguage: {} as Record<string, number> };
    const postsForTab = selectedBlogData.posts.filter((post) => post.status === tab);
    const byLanguage: Record<string, number> = {};
    for (const lang of availableLanguages) {
      byLanguage[lang.value] = postsForTab.filter((post) => post.language === lang.value).length;
    }
    return { all: postsForTab.length, byLanguage };
  }, [selectedBlogData, tab]);

  return (
    <div className='w-full h-full relative'>
      <div className='grid grid-cols-[1fr_693px_331px_1fr] grid-rows-[100px_1fr] w-full h-full'>
        <div className='flex justify-center border-dashed border-b border-[#dedede]' />
        <div className='flex justify-between items-end p-2 border-dashed border-b border-x border-[#dedede] relative'>
          <div className='size-2.75 absolute -left-1.5 -bottom-1.5 flex items-center justify-center'>
            <div className='absolute h-px w-2.75 bg-[#A1A1A1]' />
            <div className='absolute h-2.75 z-10 w-px bg-[#A1A1A1]' />
          </div>
          <div className='size-2.75 absolute -right-1.5 -bottom-1.5 flex items-center justify-center'>
            <div className='absolute h-px w-2.75 bg-[#A1A1A1]' />
            <div className='absolute h-2.75 w-px bg-[#A1A1A1] z-10' />
          </div>
          <div className='w-full flex justify-between items-end'>
            <div className='flex flex-row gap-1 items-center '>
              {/* Selector */}
              <div className='relative bg-white min-w-50 flex items-center justify-between px-2 h-9 rounded-lg border border-[#d8d8d8]'>
                <label className='flex items-center gap-1 cursor-pointer'>
                  <div className='flex items-center justify-center size-6 text-[#a0a0a0]'>
                    {loading ? (
                      <div className='animate-spin rounded-full h-4 w-4 border-2 border-[#a0a0a0] border-t-transparent' />
                    ) : (
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <circle
                          cx='12'
                          cy='12'
                          r='10'
                        ></circle>
                        <path d='M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20'></path>
                        <path d='M2 12h20'></path>
                      </svg>
                    )}
                  </div>

                  <span className='text-gray-700 text-[14px] font-medium mr-3'>
                    {selectedBlogData?.domain || ((!blogs || blogs.length === 0) && !loading && "No blogs")}
                  </span>
                </label>

                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='text-[#a0a0a0] pointer-events-none'
                >
                  <path d='m7 15 5 5 5-5'></path>
                  <path d='m7 9 5-5 5 5'></path>
                </svg>

                <select
                  value={selectedBlog}
                  onChange={(e) => setSelectedBlog(e.currentTarget.value)}
                  className='absolute inset-0 h-full w-full opacity-0 cursor-pointer appearance-none z-10'
                  aria-label='Select blog'
                >
                  {blogs?.map((blog) => (
                    <option
                      value={blog.name}
                      key={blog.name}
                    >
                      {blog.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='bg-[#e0e0e0] font-medium flex text-xs items-center rounded-lg shrink h-9 px-px border-[#e0e0e0] border-y'>
                <button
                  className={`h-full px-3 transition-all ease-linear rounded-lg cursor-pointer uppercase font-semibold ${
                    tab === "published" && "bg-white shadow ring ring-neutral-950/10"
                  }`}
                  onClick={() => setTab("published")}
                >
                  published
                </button>
                <button
                  className={`h-full px-3 transition-all ease-linear rounded-lg cursor-pointer uppercase font-semibold ${
                    tab === "draft" && "bg-white shadow ring ring-neutral-950/10"
                  }`}
                  onClick={() => setTab("draft")}
                >
                  draft
                </button>
              </div>
            </div>

            <Link
              to='/create'
              target='_blank'
              rel='noopener noreferrer'
              className={`inline-flex items-center justify-center h-9 w-fit bg-[#fbfbfb] border border-[#e0e0e0] rounded-lg font-semibold whitespace-pre text-xs text-[#21272e] gap-1 px-3 ${
                !selectedBlog ? "opacity-50 cursor-not-allowed pointer-events-none" : "cursor-pointer"
              }`}
            >
              <PlusIcon size={16} />
              <span className='uppercase'> new article </span>
            </Link>
          </div>
        </div>
        <div className='flex justify-center border-dashed border-b border-[#dedede]' />
        <div className='flex justify-center border-dashed border-b border-[#dedede]' />
        <div className='flex justify-center' />

        <div className='flex justify-center border-dashed border-x  border-[#dedede] border-b relative min-h-0 overflow-y-auto'>
          <div className='flex flex-col w-full p-2 gap-2'>
            {filteredPosts.length > 0 ? (
              filteredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/edit/${post.id}`}
                  target='_blank'
                  className='unset inline-flex items-center text-sm leading-none h-14 box-border border outline-none w-full text-[14px] text-[#21272e] bg-white border-[#e0e0e0] rounded-lg wrap-break-words resize-none placeholder:text-[#c0c0c0]'
                >
                  <div className='w-10 h-10 ml-2 border border-[#e0e1e2] flex bg-[#f0f0f0] items-center justify-center rounded-full'>
                    <BookOpenIcon size={16} />
                  </div>
                  <span className='ml-2 font-medium'>{post.title}</span>
                </Link>
              ))
            ) : (
              <div className={`flex items-center justify-center h-20 text-gray-500 text-sm ${loading && "hidden"}`}>
                {`This blog doesn't have any ${tab} posts yet`}
              </div>
            )}
          </div>
        </div>

        {/* Language selection */}
        <ul className='flex flex-col p-2 '>
          <li
            role='button'
            tabIndex={0}
            onClick={() => setLanguageFilter(null)}
            onKeyDown={(e) => e.key === "Enter" && setLanguageFilter(null)}
            className={`flex w-fit items-center gap-2 transition-colors disabled:opacity-70 disabled:cursor-default relative px-3 py-1 before:absolute before:inset-x-0 before:-top-0.5 before:h-0.5 before:rounded-t-lg before:transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:rounded-b-lg after:transition-colors disabled:hover:bg-transparent disabled:hover:before:bg-transparent disabled:hover:after:bg-transparent hover:bg-neutral-200/50 hover:before:bg-neutral-200/50 hover:after:bg-neutral-200/50 ${
              languageFilter === null ? "text-neutral-900 cursor-default" : "text-neutral-500 cursor-pointer"
            }`}
          >
            <div
              className={`w-1.5 h-1.5 rounded-full ${languageFilter === null ? "bg-neutral-900" : "border border-neutral-400  bg-neutral-300 "}`}
            />
            All articles • {languageCounts.all}
          </li>
          {availableLanguages.map((lang) => (
            <li>
              <button
                key={lang.value}
                role='button'
                disabled={!languageCounts.byLanguage[lang.value]}
                tabIndex={languageCounts.byLanguage[lang.value] ? 0 : -1}
                onClick={() => setLanguageFilter(lang.value)}
                onKeyDown={(e) => e.key === "Enter" && setLanguageFilter(lang.value)}
                className={`flex w-fit items-center gap-2 transition-colors disabled:opacity-70 disabled:cursor-default relative px-3 py-1 before:absolute before:inset-x-0 before:-top-0.5 before:h-0.5 before:rounded-t-lg before:transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:rounded-b-lg after:transition-colors  disabled:hover:bg-transparent disabled:hover:before:bg-transparent disabled:hover:after:bg-transparent hover:bg-neutral-200/50 hover:before:bg-neutral-200/50 hover:after:bg-neutral-200/50 ${
                  languageFilter === lang.value ? "text-neutral-900 cursor-default" : "text-neutral-500 cursor-pointer"
                }`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    languageFilter === lang.value ? "bg-neutral-900" : "border border-neutral-400 bg-neutral-300"
                  }`}
                />
                {lang.label} • {languageCounts.byLanguage[lang.value]}
              </button>
            </li>
          ))}
        </ul>
        <div className='flex justify-center' />

        <div className='flex justify-center' />
        <div className='flex flex-col' />
        <div className='flex justify-center' />
        <div className='flex justify-center' />
        <div className='flex justify-center' />
        <div className='flex justify-center border-dashed border-x border-[#dedede]' />
        <div className='flex justify-center' />
        <div className='flex justify-center' />
      </div>
    </div>
  );
}
